import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, message, Spin, Typography } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const { Title } = Typography;
const { TextArea } = Input;

interface WebsiteFormData {
  domain: string;
  title: string;
  description: string;
  keywords: string;
  favicon: string;
  analytics_id: string;
}

const WebsiteForm: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  useEffect(() => {
    if (id) {
      setIsEdit(true);
      fetchWebsiteData(id);
    }
  }, [id]);
  
  const fetchWebsiteData = async (websiteId: string) => {
    setInitialLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/seo/${websiteId}`);
      if (response.data.success) {
        form.setFieldsValue(response.data.data);
      } else {
        message.error(response.data.message || '获取网站数据失败');
        navigate('/websites');
      }
    } catch (error: any) {
      message.error('获取网站数据时出错: ' + (error.response?.data?.message || error.message));
      navigate('/websites');
    } finally {
      setInitialLoading(false);
    }
  };
  
  const onFinish = async (values: WebsiteFormData) => {
    setLoading(true);
    try {
      let response;
      if (isEdit) {
        response = await axios.put(`${process.env.REACT_APP_API_URL}/api/seo/${id}`, values);
      } else {
        response = await axios.post(`${process.env.REACT_APP_API_URL}/api/seo`, values);
      }
      
      if (response.data.success) {
        message.success(response.data.message || (isEdit ? '网站信息更新成功' : '网站添加成功'));
        navigate('/websites');
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error: any) {
      if (error.response?.data?.errors) {
        // 显示表单验证错误
        error.response.data.errors.forEach((err: any) => {
          message.error(`${err.path}: ${err.message}`);
        });
      } else {
        message.error('操作失败: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setLoading(false);
    }
  };
  
  if (initialLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" tip="加载网站数据..." />
      </div>
    );
  }
  
  return (
    <div>
      <Title level={2}>{isEdit ? '编辑网站' : '添加网站'}</Title>
      
      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            description: '',
            keywords: '',
            favicon: '',
            analytics_id: '',
          }}
        >
          <Form.Item
            name="domain"
            label="域名"
            rules={[
              { required: true, message: '请输入域名' },
              { min: 3, message: '域名长度不能少于3个字符' },
            ]}
            extra="如: example.com"
          >
            <Input disabled={isEdit} placeholder="请输入域名" />
          </Form.Item>
          
          <Form.Item
            name="title"
            label="网站标题"
            rules={[
              { required: true, message: '请输入网站标题' },
              { max: 100, message: '标题不能超过100个字符' },
            ]}
          >
            <Input placeholder="请输入网站标题" />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="网站描述"
            rules={[
              { max: 200, message: '描述不能超过200个字符' },
            ]}
          >
            <TextArea rows={4} placeholder="请输入网站描述" />
          </Form.Item>
          
          <Form.Item
            name="keywords"
            label="关键词"
            rules={[
              { max: 200, message: '关键词不能超过200个字符' },
            ]}
            extra="多个关键词用逗号分隔"
          >
            <Input placeholder="请输入关键词，多个关键词用逗号分隔" />
          </Form.Item>
          
          <Form.Item
            name="favicon"
            label="网站图标URL"
          >
            <Input placeholder="请输入网站图标URL" />
          </Form.Item>
          
          <Form.Item
            name="analytics_id"
            label="统计ID"
            extra="如Google Analytics ID或百度统计ID"
          >
            <Input placeholder="请输入统计ID" />
          </Form.Item>
          
          <Form.Item className="form-actions">
            <Button onClick={() => navigate('/websites')}>
              取消
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {isEdit ? '更新' : '添加'}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default WebsiteForm; 